// Dashboard editor per mockups/07-dashboard-edit.html: client-facing
// preview card (inline-editable), phase rows with status pills, and a
// publish-pending model — edits accumulate locally and write once via
// "Publish changes" (GET → merge → PUT on the worker, so webhook-fed
// sections survive). Fixed 4 phases per Henry (no rename/add/reorder until
// hsp-dashboards supports it); "Archived" status skipped — no manifest flag.
// Webhook-fed sections (scripts, call sheets, review rounds, final files,
// invoices) and the danger-zone clears act immediately through the proxy,
// exactly like the legacy page.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { Manifest } from '../../shared/manifest-types';
import { retainLaterEdits, type EditorManifest } from '../lib/dashboardComms';
import { DashboardEditComms } from '../components/dashboardComms/DashboardComms';
import {
  Dropdown,
  formatDate,
  Group,
  Pill,
  Modal,
  TrashAction,
} from '../components/primitives';
import { DASHBOARD_BASE, DASHBOARD_PHASES, ToggleChip } from './Dashboards';
import { ManifestTools } from '../components/ManifestTools';
import { DashboardEditMobile } from '../components/DashboardEditMobile';
import { useIsMobile } from '../lib/useIsMobile';

interface ResourceRow {
  title: string;
  url: string;
}
interface FileRow {
  name: string;
  url: string;
  kind: string;
}
interface VideoRow {
  id: string;
  title: string;
  phase: string;
  completed_date: string;
}

interface Draft {
  client_name: string;
  project_name: string;
  project_summary: string;
  contact_name: string;
  contact_email: string;
  current_phase: string;
  henry_note_body: string;
  henry_note_date: string;
  show_production: boolean;
  show_review: boolean;
  show_commercial: boolean;
  resources: ResourceRow[];
  files: FileRow[];
  videos: VideoRow[];
}

function draftFrom(m: Manifest): Draft {
  return {
    client_name: m.client_name,
    project_name: m.project_name ?? '',
    project_summary: m.project_summary ?? '',
    contact_name: m.primary_contact?.name ?? '',
    contact_email: m.primary_contact?.email ?? '',
    current_phase: m.current_phase ?? 'pre-production',
    henry_note_body: m.henry_note?.body ?? '',
    henry_note_date: m.henry_note?.posted_at?.slice(0, 10) ?? '',
    show_production: m.production?.visible !== false,
    show_review: m.review?.visible !== false,
    show_commercial: m.commercial?.visible !== false,
    resources: (m.commercial?.resources ?? []).map((r) => ({ title: r.title, url: r.url ?? '' })),
    files: (m.files ?? []).map((f) => ({ name: f.name, url: f.url, kind: f.kind ?? '' })),
    videos: (m.production?.videos ?? []).map((v) => ({
      id: v.id,
      title: v.title,
      phase: v.phase,
      completed_date: v.completed_date ?? '',
    })),
  };
}

/** Count fields that differ from the published manifest. */
function pendingEdits(draft: Draft, baseline: Draft): number {
  return (Object.keys(baseline) as (keyof Draft)[]).filter(
    (k) => JSON.stringify(draft[k]) !== JSON.stringify(baseline[k]),
  ).length;
}

/** Click-to-edit text inside the preview card — keeps the card's own type. */
function PreviewText({
  value,
  placeholder,
  className,
  inputClass,
  onSave,
}: {
  value: string;
  placeholder: string;
  className: string;
  /** e.g. "inline-input" for rail fields; preview-card styles its own. */
  inputClass?: string;
  onSave: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  if (editing) {
    return (
      <div className={className}>
        <input
          className={inputClass}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSave(text.trim());
              setEditing(false);
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          onBlur={() => {
            onSave(text.trim());
            setEditing(false);
          }}
          autoFocus
        />
      </div>
    );
  }
  return (
    <div
      className={`${className} editable-text`}
      onClick={() => {
        setText(value);
        setEditing(true);
      }}
    >
      {value || <span style={{ color: 'var(--text-faint)' }}>{placeholder}</span>}
    </div>
  );
}

/** Compact editable rows for resources / files / videos — saved on publish. */
function DraftListEditor<T extends Record<string, string>>({
  rows,
  cols,
  empty,
  addLabel,
  onChange,
}: {
  rows: T[];
  cols: { key: keyof T & string; placeholder: string; width?: number }[];
  empty: T;
  addLabel: string;
  onChange: (rows: T[]) => void;
}) {
  return (
    <>
      {rows.map((row, i) => (
        <div className="phase-row" key={i} style={{ gap: 8 }}>
          {cols.map((col) => (
            <input
              key={col.key}
              className="f-input"
              style={{ flex: col.width ? `0 0 ${col.width}px` : 1, height: 28, fontSize: 12 }}
              placeholder={col.placeholder}
              value={row[col.key]}
              onChange={(e) =>
                onChange(rows.map((r, j) => (j === i ? { ...r, [col.key]: e.target.value } : r)))
              }
            />
          ))}
          <button
            type="button"
            className="btn"
            style={{ color: 'var(--text-faint)', borderColor: 'transparent', padding: '0 6px' }}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            aria-label="Remove row"
          >
            ×
          </button>
        </div>
      ))}
      <div className="quick-add" onClick={() => onChange([...rows, { ...empty }])}>
        <span className="plus">+</span>
        <span>{addLabel}</span>
      </div>
    </>
  );
}

const ROUND_STATUSES = ['draft', 'in_review', 'approved'];

export default function DashboardEdit() {
  const { slug } = useParams<{ slug: string }>();
  const isMobile = useIsMobile();
  const activeSlug = useRef(slug); activeSlug.current = slug;
  const [manifest, setManifest] = useState<EditorManifest | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [baseline, setBaseline] = useState<Draft | null>(null);
  const [draftEtag,setDraftEtag] = useState<string|null>(null);
  const draftRef = useRef(draft); draftRef.current = draft;
  const baselineRef = useRef(baseline); baselineRef.current = baseline;
  const [error, setError] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stale,setStale] = useState(false);
  const [comparison,setComparison] = useState<EditorManifest|null>(null);
  const [phaseMenu, setPhaseMenu] = useState<string | null>(null);
  const [roundMenu, setRoundMenu] = useState<number | null>(null);

  const fetchManifest = useCallback(async (): Promise<EditorManifest | null> => {
    try {
      const m = await api<EditorManifest>(`/api/admin/dashboards/manifest/${slug}`);
      if (activeSlug.current !== slug) return null;
      setError(null);
      setUnconfigured(false);
      setMissing(false);
      return m;
    } catch (e) {
      if (activeSlug.current !== slug) return null;
      if (e instanceof ApiError && e.status === 503) setUnconfigured(true);
      else if (e instanceof ApiError && e.status === 404) setMissing(true);
      else setError(e instanceof Error ? e.message : 'Failed to load.');
      return null;
    }
  }, [slug]);

  // Initial navigation establishes a draft. Immediate proxy actions below
  // use refreshManifestOnly so webhook-derived sections update without
  // overwriting unpublished local edits.
  useEffect(() => {
    let cancelled = false;
    setManifest(null); setDraft(null); setBaseline(null); setDraftEtag(null); setStale(false); setComparison(null);
    void fetchManifest().then((m) => {
      if (!m || cancelled) return;
      const nextDraft = draftFrom(m);
      setManifest(m);
      setDraft(nextDraft);
      setBaseline(nextDraft);
      setDraftEtag(m._etag ?? null);
    });
    return () => { cancelled = true; };
  }, [fetchManifest]);

  const refreshManifestOnly = useCallback(async () => {
    const m = await fetchManifest();
    if (m) {
      setManifest(m);
      const live = draftRef.current, base = baselineRef.current;
      if (!live || !base || pendingEdits(live,base) === 0) {
        const next = draftFrom(m); setDraft(next); setBaseline(next); setDraftEtag(m._etag ?? null);
      }
    }
  }, [fetchManifest]);


  const pending = draft && baseline ? pendingEdits(draft, baseline) : 0;

  // Navigation guard (Phase 1.9): the batch-publish model means edits sit
  // local until "Publish". Warn before a refresh/close/external nav loses them.
  // (In-app route changes are mitigated by the loud banner below; a full
  // client-nav blocker would need the react-router data-router.)
  useEffect(() => {
    if (pending === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [pending]);

  const publish = async () => {
    if (!draft || busy) return;
    const submitted = draft;
    setBusy(true);
    setError(null);
    try {
      if (!draftEtag) throw new Error('Reload this dashboard before publishing; its version is unavailable. Your edits are retained.');
      await api(`/api/admin/dashboards/edit/${slug}`, { method: 'POST', json: { ...submitted, _etag: draftEtag } });
      const m = await fetchManifest();
      if (!m) throw new Error('Published, but could not reload the dashboard. Reload before making another change.');
      const published = draftFrom(m);
      setManifest(m); setBaseline(published); setDraft(retainLaterEdits(submitted,draftRef.current ?? submitted,published)); setDraftEtag(m._etag ?? null); setStale(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setStale(true);
      setError(e instanceof Error ? e.message : 'Publish failed.');
    } finally {
      setBusy(false);
    }
  };

  const proxyDelete = async (path: string, confirmText: string) => {
    if (!window.confirm(confirmText)) return;
    setError(null);
    try {
      await api(`/api/admin/dashboards/proxy/${path}`, { method: 'DELETE' });
      await refreshManifestOnly();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    }
  };

  const setRoundStatus = async (round: number, status: string) => {
    try {
      await api(`/api/admin/dashboards/proxy/manifest/${slug}/review/rounds/${round}`, {
        method: 'PATCH',
        json: { status },
      });
      await refreshManifestOnly();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed.');
    }
  };

  const reviewLatest = async () => {
    const latest = await fetchManifest();
    if (latest) setComparison(latest);
  };
  const keepDraftOnLatest = () => {
    if (!comparison || !draft || !baseline) return;
    const latest = draftFrom(comparison);
    setDraft(retainLaterEdits(baseline,draft,latest)); setBaseline(latest);
    setManifest(comparison); setDraftEtag(comparison._etag ?? null);
    setStale(false); setComparison(null); setError(null);
  };
  const recovery = stale ? <div className="cc-message warn" style={{margin:'12px 20px'}}>
    The published dashboard changed. Your edits are retained. Review the latest version before publishing.
    <button className="btn" style={{marginLeft:12}} onClick={()=>void reviewLatest()} disabled={busy}>Review latest dashboard</button>
  </div> : null;
  const comparisonModal = comparison && draft && baseline ? <Modal title="Review latest dashboard" context="PUBLISH CONFLICT" onClose={()=>setComparison(null)} foot={<><button className="btn" onClick={()=>setComparison(null)}>Keep reviewing</button><button className="btn primary" onClick={keepDraftOnLatest}>Keep my edits on latest version</button></>}>
    <p className="cc-note">These fields changed in your draft or on the published dashboard. Keeping your edits adopts the latest version; you still choose when to publish.</p>
    {(Object.keys(baseline) as (keyof Draft)[]).filter(k=>JSON.stringify(draft[k])!==JSON.stringify(baseline[k])||JSON.stringify(draftFrom(comparison)[k])!==JSON.stringify(baseline[k])).map(k=><div key={k} className="cc-merge-row"><strong>{k.replaceAll('_',' ')}</strong><div><span>Published</span><pre>{typeof draftFrom(comparison)[k]==='string'?String(draftFrom(comparison)[k]):JSON.stringify(draftFrom(comparison)[k],null,2)}</pre></div><div><span>My draft</span><pre>{typeof draft[k]==='string'?String(draft[k]):JSON.stringify(draft[k],null,2)}</pre></div></div>)}
  </Modal> : null;

  if (unconfigured || missing || (error && !manifest)) {
    return (
      <div className="page-body">
        <div className="content">
          <div className="empty-note">
            {missing
              ? 'Dashboard not found.'
              : unconfigured
                ? 'Dashboard integration not configured — set DASHBOARD_WRITE_SECRET on this worker.'
                : error}
          </div>
        </div>
      </div>
    );
  }
  if (!manifest || !draft || (manifest.slug && manifest.slug !== slug)) return <div className="page-body" />;

  // Phone framing stays compact; client comms uses the same forms at both widths.
  if (isMobile) {
    return (
      <DashboardEditMobile
        slug={slug ?? ''}
        manifest={manifest}
        fields={draft}
        pending={pending}
        busy={busy}
        error={error}
        onPublish={() => void publish()}
        onToolsApplied={refreshManifestOnly}
        comms={overview => <>{recovery}{comparisonModal}<DashboardEditComms key={slug} slug={slug ?? ''} manifest={manifest} onChanged={refreshManifestOnly}>{overview}</DashboardEditComms></>}
      />
    );
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const currentIdx = DASHBOARD_PHASES.findIndex((p) => p.key === draft.current_phase);
  const phaseStatus = (i: number): { label: string; tone?: 'accent' } =>
    i < currentIdx ? { label: 'Done' } : i === currentIdx ? { label: 'Active', tone: 'accent' } : { label: 'Pending' };

  // Picking a status derives the new current phase: Active = this phase,
  // Done = the next one opens, Pending = fall back to the one before.
  const pickPhaseStatus = (i: number, status: string) => {
    const last = DASHBOARD_PHASES.length - 1;
    const idx = status === 'Active' ? i : status === 'Done' ? Math.min(i + 1, last) : Math.max(i - 1, 0);
    set('current_phase', DASHBOARD_PHASES[idx].key);
  };

  const scripts = manifest.production?.scripts ?? [];
  const callSheets = manifest.production?.call_sheets ?? [];
  const rounds = manifest.review?.rounds ?? [];
  const finalFiles = manifest.review?.final_files ?? [];
  const hubInvoices = manifest.commercial?.invoices ?? [];
  const lastPublished = manifest.meta?.generated_at?.slice(0, 10) ?? null;

  const linkedProjectName = manifest.project_name ?? null;

  return (
    <>
      <header className="detail-header">
        <Link className="crumb" to="/dashboards">
          Dashboards
        </Link>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">{manifest.client_name}</span>
        <span style={{ marginLeft: 6 }}>
          <Pill tone="accent">Live</Pill>
        </span>
        <div className="detail-actions">
          <a className="btn" href={`${DASHBOARD_BASE}/${slug}?internal=1`} target="_blank" rel="noreferrer">
            Internal preview ↗
          </a>
          <button className="btn primary" onClick={publish} disabled={busy || pending === 0}>
            {busy ? 'Publishing…' : 'Publish changes →'}
          </button>
        </div>
      </header>

      {/* Unpublished-changes banner (Phase 1.9) — makes the batch-publish
          "dirty draft" state loud so edits don't sit unseen by the client. */}
      {pending > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 20px',
            background: 'var(--gold-soft)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--gold)',
            }}
          >
            Unpublished
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
            {pending} change{pending === 1 ? '' : 's'} not yet live — clients won’t see them until you publish.
          </span>
          <button
            className="btn primary"
            style={{ marginLeft: 'auto' }}
            onClick={publish}
            disabled={busy}
          >
            {busy ? 'Publishing…' : 'Publish now →'}
          </button>
        </div>
      )}

      {recovery}{comparisonModal}
      <ManifestTools slug={slug!} onApplied={refreshManifestOnly} disabled={pending > 0 || busy} />
      <DashboardEditComms key={slug} slug={slug ?? ''} manifest={manifest} onChanged={refreshManifestOnly}>
      <div className="page-body">
        <div className="detail-grid">
          <div className="detail-main">
            <h1 className="detail-name">{manifest.client_name}</h1>
            <div className="detail-sub">
              Client-facing progress page{linkedProjectName ? ` · linked to ${linkedProjectName}` : ''}
            </div>
            {error && <p className="error-text" style={{ marginTop: 12 }}>{error}</p>}

            <Group title="Client-facing header">
              <div className="preview-card">
                <div className="preview-eyebrow">Production status</div>
                <PreviewText
                  value={draft.project_name}
                  placeholder="Project name…"
                  className="preview-title"
                  onSave={(v) => set('project_name', v)}
                />
                <PreviewText
                  value={draft.project_summary}
                  placeholder="One-line summary the client sees…"
                  className="preview-sub"
                  onSave={(v) => set('project_summary', v)}
                />
              </div>
              <div className="notes-hint">Click any text to edit it inline, then publish your changes</div>
            </Group>

            <Group title="Phases" count={DASHBOARD_PHASES.length}>
              {DASHBOARD_PHASES.map((p, i) => {
                const status = phaseStatus(i);
                return (
                  <div className="phase-row" key={p.key}>
                    <span className="phase-num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="phase-label">{p.label}</span>
                    <span className="phase-status">
                      <span className="dropdown-anchor">
                        <span className="select-pill" onClick={() => setPhaseMenu((k) => (k === p.key ? null : p.key))}>
                          <Pill tone={status.tone}>{status.label}</Pill>
                          <span className="caret">▾</span>
                        </span>
                        {phaseMenu === p.key && (
                          <Dropdown
                            items={['Done', 'Active', 'Pending'].map((s) => ({ value: s, label: s }))}
                            value={status.label}
                            onSelect={(s) => pickPhaseStatus(i, s)}
                            onClose={() => setPhaseMenu(null)}
                          />
                        )}
                      </span>
                    </span>
                  </div>
                );
              })}
            </Group>

            <Group title="Henry's note">
              <textarea
                className="notes-box"
                style={{ width: '100%', minHeight: 70, resize: 'vertical', outline: 'none' }}
                placeholder="Optional signed note shown in the client hero — clear to remove."
                value={draft.henry_note_body}
                onChange={(e) => set('henry_note_body', e.target.value)}
              />
              <div className="notes-hint">Publishes on save · shown with your name on the dashboard</div>
            </Group>

            {scripts.length > 0 && (
              <Group title="Scripts" count={scripts.length}>
                {scripts.map((s, i) => (
                  <div className="row" key={i} style={{ cursor: 'default' }}>
                    <span className="row-title" title={s.title || 'Untitled script'}>{s.title || 'Untitled script'}</span>
                    <span className="row-context" title={s.status ?? 'draft'}>{s.status ?? 'draft'}</span>
                    <span className="row-spacer" />
                    {s.url && (
                      <a className="col-view" href={s.url} target="_blank" rel="noreferrer">
                        Open ↗
                      </a>
                    )}
                    {s.scriptgen_script_id && (
                      <button
                        type="button"
                        className="btn"
                        style={{ color: 'var(--text-faint)', borderColor: 'transparent', padding: '0 6px' }}
                        onClick={() =>
                          proxyDelete(
                            `manifest/${slug}/scripts/${encodeURIComponent(s.scriptgen_script_id!)}`,
                            `Remove script "${s.title}" from the dashboard?`,
                          )
                        }
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </Group>
            )}

            {callSheets.length > 0 && (
              <Group title="Call sheets" count={callSheets.length}>
                {callSheets.map((cs, i) => (
                  <div className="row" key={i} style={{ cursor: 'default' }}>
                    <span className="row-title" title={cs.title || 'Untitled call sheet'}>
                      {cs.title || 'Untitled call sheet'}
                    </span>
                    <span className="row-context" title={cs.shoot_date ?? ''}>{cs.shoot_date ?? ''}</span>
                    <span className="row-spacer" />
                    {cs.url && (
                      <a className="col-view" href={cs.url} target="_blank" rel="noreferrer">
                        Open ↗
                      </a>
                    )}
                    {cs.callsheet_id && (
                      <button
                        type="button"
                        className="btn"
                        style={{ color: 'var(--text-faint)', borderColor: 'transparent', padding: '0 6px' }}
                        onClick={() =>
                          proxyDelete(
                            `manifest/${slug}/call-sheets/${encodeURIComponent(cs.callsheet_id!)}`,
                            `Remove call sheet "${cs.title}"?`,
                          )
                        }
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </Group>
            )}

            {rounds.length > 0 && (
              <Group title="Review rounds" count={rounds.length}>
                {rounds.map((r) => (
                  <div key={r.round}>
                    <div className="row" style={{ cursor: 'default' }}>
                      <span className="row-title" title={`Round ${r.round}${r.label ? ` — ${r.label}` : ''}`}>
                        Round {r.round}
                        {r.label ? ` — ${r.label}` : ''}
                      </span>
                      <span className="row-spacer" />
                      <span className="dropdown-anchor">
                        <span
                          className="select-pill"
                          onClick={() => setRoundMenu((n) => (n === r.round ? null : r.round))}
                        >
                          <Pill tone={r.status === 'approved' ? 'accent' : undefined}>
                            {(r.status ?? 'in_review').replace(/_/g, ' ')}
                          </Pill>
                          <span className="caret">▾</span>
                        </span>
                        {roundMenu === r.round && (
                          <Dropdown
                            items={ROUND_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
                            value={r.status ?? 'in_review'}
                            onSelect={(s) => setRoundStatus(r.round, s)}
                            onClose={() => setRoundMenu(null)}
                          />
                        )}
                      </span>
                      <button
                        type="button"
                        className="btn"
                        style={{ color: 'var(--text-faint)', borderColor: 'transparent', padding: '0 6px' }}
                        onClick={() =>
                          proxyDelete(`manifest/${slug}/review/rounds/${r.round}`, `Remove review round ${r.round}?`)
                        }
                      >
                        ×
                      </button>
                    </div>
                    {(r.videos ?? []).map((v, vi) => (
                      <div className="row" key={vi} style={{ height: 30, cursor: 'default', paddingLeft: 18 }}>
                        <span className="row-context" title={v.title}>{v.title}</span>
                        <span className="row-spacer" />
                        {v.frameio_share_id && (
                          <button
                            type="button"
                            className="btn"
                            style={{ color: 'var(--text-faint)', borderColor: 'transparent', padding: '0 6px' }}
                            onClick={() =>
                              proxyDelete(
                                `manifest/${slug}/review/videos/${encodeURIComponent(v.frameio_share_id!)}`,
                                `Remove video "${v.title}" from round ${r.round}?`,
                              )
                            }
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </Group>
            )}

            {finalFiles.length > 0 && (
              <Group title="Final files" count={finalFiles.length}>
                {finalFiles.map((f, i) => (
                  <div className="row" key={i} style={{ cursor: 'default' }}>
                    <span className="row-title" title={f.title}>{f.title}</span>
                    <span className="row-context" title={f.format ?? ''}>{f.format ?? ''}</span>
                    <span className="row-spacer" />
                    {f.url && (
                      <a className="col-view" href={f.url} target="_blank" rel="noreferrer">
                        Open ↗
                      </a>
                    )}
                    {f.frameio_share_id && (
                      <button
                        type="button"
                        className="btn"
                        style={{ color: 'var(--text-faint)', borderColor: 'transparent', padding: '0 6px' }}
                        onClick={() =>
                          proxyDelete(
                            `manifest/${slug}/final-files/${encodeURIComponent(f.frameio_share_id!)}`,
                            `Remove final file "${f.title}"?`,
                          )
                        }
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </Group>
            )}

            {hubInvoices.length > 0 && (
              <Group title="Dashboard invoices" count={hubInvoices.length}>
                {hubInvoices.map((inv, i) => (
                  <div className="row" key={i} style={{ cursor: 'default' }}>
                    <span className="row-title" title={inv.label}>{inv.label}</span>
                    <span
                      className="row-context"
                      title={[inv.amount, inv.status ?? 'unpaid'].filter(Boolean).join(' · ')}
                    >
                      {[inv.amount, inv.status ?? 'unpaid'].filter(Boolean).join(' · ')}
                    </span>
                    <span className="row-spacer" />
                    {inv.ghl_invoice_id && (
                      <button
                        type="button"
                        className="btn"
                        style={{ color: 'var(--text-faint)', borderColor: 'transparent', padding: '0 6px' }}
                        onClick={() =>
                          proxyDelete(
                            `manifest/${slug}/invoices/${encodeURIComponent(inv.ghl_invoice_id!)}`,
                            `Remove invoice "${inv.label}"?`,
                          )
                        }
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </Group>
            )}

            <Group title="Resources">
              <DraftListEditor
                rows={draft.resources}
                cols={[
                  { key: 'title', placeholder: 'Title' },
                  { key: 'url', placeholder: 'URL (optional)' },
                ]}
                empty={{ title: '', url: '' }}
                addLabel="Add resource…"
                onChange={(resources) => set('resources', resources)}
              />
            </Group>

            <Group title="Client files">
              <DraftListEditor
                rows={draft.files}
                cols={[
                  { key: 'name', placeholder: 'Name' },
                  { key: 'url', placeholder: 'URL' },
                  { key: 'kind', placeholder: 'Kind', width: 90 },
                ]}
                empty={{ name: '', url: '', kind: '' }}
                addLabel="Add file…"
                onChange={(files) => set('files', files)}
              />
            </Group>

            <Group title="Videos">
              <DraftListEditor
                rows={draft.videos}
                cols={[
                  { key: 'id', placeholder: 'ID', width: 110 },
                  { key: 'title', placeholder: 'Title' },
                  { key: 'phase', placeholder: 'Phase key', width: 120 },
                ]}
                empty={{ id: `video-${draft.videos.length + 1}`, title: '', phase: 'pre-production', completed_date: '' }}
                addLabel="Add video…"
                onChange={(videos) => set('videos', videos)}
              />
            </Group>

            <Group title="Danger zone">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                {(
                  [
                    ['Clear intake', 'intake', !manifest.intake],
                    ['Clear testimonial', 'testimonial', !manifest.testimonial],
                    ['Clear videos', 'videos', !manifest.production?.videos?.length],
                    ["Clear Henry's note", 'henry-note', !manifest.henry_note],
                  ] as const
                ).map(([label, path, disabled]) =>
                  disabled ? null : (
                    <TrashAction
                      key={path}
                      label={label}
                      onConfirm={() => {
                        api(`/api/admin/dashboards/proxy/manifest/${slug}/${path}`, { method: 'DELETE' })
                          .then(refreshManifestOnly)
                          .catch((e) => setError(e instanceof Error ? e.message : 'Delete failed.'));
                      }}
                    />
                  ),
                )}
              </div>
              <div className="notes-hint">Clears webhook/client-submitted state on the live dashboard</div>
            </Group>
          </div>

          <div className="detail-rail">
            <div className="field">
              <div className="field-label">Internal preview</div>
              <div className="field-value mono">
                <a href={`${DASHBOARD_BASE}/${slug}?internal=1`} target="_blank" rel="noreferrer">
                  …/{slug} ↗
                </a>
              </div>
            </div>
            <div className="field">
              <div className="field-label">Client</div>
              <div className="field-value">{manifest.client_name}</div>
            </div>
            <div className="field">
              <div className="field-label">Linked project</div>
              <div className="field-value">
                {linkedProjectName ?? <span style={{ color: 'var(--text-faint)' }}>—</span>}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Contact</div>
              <PreviewText
                value={draft.contact_name}
                placeholder="Contact name…"
                className="field-value"
                inputClass="inline-input"
                onSave={(v) => set('contact_name', v)}
              />
              <PreviewText
                value={draft.contact_email}
                placeholder="Contact email…"
                className="field-value mono"
                inputClass="inline-input mono"
                onSave={(v) => set('contact_email', v)}
              />
            </div>
            <div className="field">
              <div className="field-label">Sections shown</div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 2 }}>
                {(['production', 'review', 'commercial'] as const).map((key) => {
                  const k = `show_${key}` as const;
                  return <ToggleChip key={key} label={key} on={draft[k]} onToggle={() => set(k, !draft[k])} />;
                })}
              </div>
            </div>
            <div className="field">
              <div className="field-label">Last published</div>
              <div className="field-value mono">{lastPublished ? formatDate(lastPublished) : '—'}</div>
            </div>
            <div className="field">
              <div className="field-label">Unpublished changes</div>
              <div className="field-value" style={{ color: pending > 0 ? 'var(--gold)' : 'var(--text-faint)' }}>
                {pending > 0 ? `${pending} edit${pending === 1 ? '' : 's'} pending` : 'None'}
              </div>
            </div>
          </div>
        </div>
      </div>
      </DashboardEditComms>
    </>
  );
}
